import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

export interface DatacenterConfig {
  id: number;
  code: string;
  name: string;
  rows_count: number;
  max_racks_per_row: number;
  rack_step_m: number;
  aisle_width_m: number;
  rack_depth_m: number;
  rack_height_m: number;
  tray_above_rack_m: number;
  cable_slack_m: number;
  rack_naming_template: string;
  prefix: string;
  routing_strategy: string;
  cable_trays_json: any;
  aisle_zones_json?: any;
  ai_enabled: boolean;
}

export interface Draft {
  id: number;
  dc_code: string;
  name: string;
  rows_count?: number;
  max_racks_per_row?: number;
  racks_json?: any;
  cable_trays_json?: any;
  published: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Template {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_public: boolean;
}

export function useDatacenter(code: string) {
  return useQuery({
    queryKey: ['datacenter', code],
    queryFn: () => api.get(`/api/datacenters/${code}`).then(r => r.data as DatacenterConfig),
    enabled: !!code,
  });
}

export function useDrafts(code: string) {
  return useQuery({
    queryKey: ['drafts', code],
    queryFn: () => api.get(`/api/datacenters/${code}/drafts`).then(r => r.data as Draft[]),
    enabled: !!code,
  });
}

export function useTemplates(code: string) {
  return useQuery({
    queryKey: ['templates', code],
    queryFn: () => api.get(`/api/datacenters/${code}/templates`).then(r => r.data as Template[]),
    enabled: !!code,
  });
}

export function useCreateDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (draft: Partial<Draft>) =>
      api.post(`/api/datacenters/${code}/drafts`, draft).then(r => r.data as Draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts', code] }),
  });
}

export function useUpdateDraft(code: string) {
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ draftId, racks_json, cable_trays_json }: {
      draftId: number;
      racks_json: any;
      cable_trays_json: any;
    }) => api.put(`/api/datacenters/${code}/drafts/${draftId}`, {
      racks_json,
      cable_trays_json,
    }).then(r => r.data),
  });
}

export function usePublishDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) =>
      api.post(`/api/datacenters/${code}/drafts/${draftId}/publish`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['datacenter', code] });
    },
  });
}

export function useDeleteDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) =>
      api.delete(`/api/datacenters/${code}/drafts/${draftId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts', code] }),
  });
}

export function useApplyTemplate(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateCode: string) =>
      api.post(`/api/datacenters/${code}/templates/${templateCode}/apply`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['datacenter', code] });
    },
  });
}

export function useExportDatacenter(code: string) {
  return useMutation({
    mutationFn: () =>
      api.post(`/api/datacenters/${code}/export`).then(r => r.data),
  });
}
